// 枚举类型定义
export enum GenderType {
  MALE = 1,
  FEMALE = 2,
}

export enum StaffState {
  ACTIVE = 1,
  RESIGNED = 2,
  PENDING = 3,
}

export enum StaffRole {
  SERVICE_STAFF = 1,
  MANAGER = 2,
  ADMIN = 3,
}

// API 请求类型
export interface StaffSearchParams {
  page?: number;
  limit?: number;
  name?: string;
  phone?: string;
  idCard?: string;
  gender?: GenderType;
  address?: string;
  customerName?: string;
  state?: StaffState;
  joinDateStart?: string; // YYYY-MM-DD 格式
  joinDateEnd?: string; // YYYY-MM-DD 格式
}

// 员工响应数据类型
export interface StaffResponse {
  id: number;
  commonUserId?: number;
  name?: string;
  avatar?: string;
  gender?: GenderType;
  age?: number;
  phone?: string;
  address?: string;
  idCard?: string;
  idCardImages?: string[]; // 身份证图片数组
  healthCertImage?: string; // 健康证图片
  onBoard?: string; // ISO 日期字符串
  state: StaffState;
  role: StaffRole;
  // 统计数据字段（直接在根级别，与后端DTO保持一致）
  serviceCount: number;
  customerCount: number;
  rating: number;
  title?: string; // 职位标签
  extra?: {
    idNumber?: string;
    idPhoto?: string;
    healthCertificate?: string;
    bankCard?: string;
    emergencyContact?: string;
    emergencyPhone?: string;
  };
  createdAt: string;
  updatedAt: string;
}

// 员工审核表单数据
export interface StaffApproveFormData {
  name: string;
  gender: GenderType;
  age: number;
  address: string;
  phone: string;
  idCard: string;
  joinDate: string; // YYYY-MM-DD 格式
  title?: string; // 职位标签
}

// 员工离职表单数据
export interface StaffLeaveFormData {
  leaveDate: string; // YYYY-MM-DD 格式
  leaveReason: string;
}

// API 响应包装类型
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

// 分页响应类型
export interface PaginatedResponse<T> {
  items: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

// 员工列表API响应
export interface StaffListResponse
  extends ApiResponse<PaginatedResponse<StaffResponse>> {}

// 员工操作API响应
export interface StaffActionResponse extends ApiResponse<StaffResponse> {}

// 图片上传相关类型（替换原有的UploadFiles接口）
export interface ImageUploadValues {
  avatar?: string;
  idCardFront?: string;
  idCardBack?: string;
  healthCert?: string;
}

// 前端显示用的员工数据（兼容原有组件）
export interface StaffMember {
  id: number;
  name: string;
  avatar: string;
  gender: "男" | "女";
  age: number;
  phone: string;
  address: string;
  idCard: string;
  idCardImage: string; // 身份证正面图片（用于显示）
  idCardImages?: string[]; // 完整的身份证图片数组（用于编辑）
  healthCertImage: string;
  joinDate: string;
  serviceCount: number;
  customerCount: number;
  rating: string;
  status: "待审核" | "在职" | "已离职";
  title?: string; // 职位标签
}

// 数据转换函数：服务端数据转换为前端显示数据
export function convertStaffResponseToMember(
  response: StaffResponse
): StaffMember {
  return {
    id: response.id,
    name: response.name || "未知",
    avatar: response.avatar || "",
    gender: response.gender === GenderType.MALE ? "男" : "女",
    age: response.age || 0,
    phone: response.phone || "",
    address: response.address || "",
    idCard: response.idCard || "",
    idCardImage: response.idCardImages?.[0] || "", // 取第一张身份证图片用于显示
    idCardImages: response.idCardImages || [], // 保留完整的身份证图片数组用于编辑
    healthCertImage: response.healthCertImage || "",
    joinDate: response.onBoard
      ? new Date(response.onBoard).toLocaleDateString()
      : "",
    serviceCount: response.serviceCount || 0,
    customerCount: response.customerCount || 0,
    rating: response.rating?.toString() || "0",
    status:
      response.state === StaffState.ACTIVE
        ? "在职"
        : response.state === StaffState.PENDING
        ? "待审核"
        : "已离职",
    title: response.title || "",
  };
}

// 客户搜索参数
export interface CustomerSearchParams {
  phone: string;
  page?: number;
  limit?: number;
}

// 客户响应数据类型
export interface CustomerResponse {
  id: number;
  commonUserId?: number;
  name?: string;
  avatar?: string;
  gender: GenderType;
  genderDisplay: string;
  phone?: string;
  createdAt: string;
}

// 客户搜索API响应
export interface CustomerSearchResponse
  extends ApiResponse<PaginatedResponse<CustomerResponse>> {}

// 新增员工表单数据（继承审核表单数据，并添加commonUserId）
export interface StaffCreateFormData extends StaffApproveFormData {
  commonUserId?: number;
}

// 前端表单数据转换为API数据
export function convertFormToApiData(formData: any): StaffApproveFormData {
  return {
    name: formData.name,
    gender: formData.gender === "男" ? GenderType.MALE : GenderType.FEMALE,
    age: formData.age,
    address: formData.address,
    phone: formData.phone,
    idCard: formData.idCard,
    joinDate: formData.joinDate.format("YYYY-MM-DD"),
    title: formData.title,
  };
}

// 新增员工表单数据转换为API数据
export function convertCreateFormToApiData(
  formData: any,
  commonUserId?: number
): StaffCreateFormData {
  return {
    ...convertFormToApiData(formData),
    commonUserId,
  };
}

// 修改员工统计表单数据
export interface StaffStatsFormData {
  serviceCount: number;
  customerCount: number;
  rating: string;
}

// 修改员工统计API响应
export interface StaffStatsUpdateResponse extends ApiResponse<StaffResponse> {}

// 修改员工资料表单数据（与审核表单相同）
export interface StaffProfileFormData {
  name: string;
  gender: GenderType;
  age: number;
  address: string;
  phone: string;
  idCard: string;
  joinDate: string;
  title?: string; // 职位标签
}

// 修改员工资料API响应
export interface StaffProfileUpdateResponse
  extends ApiResponse<StaffResponse> {}

// 可用服务人员类型（从原mockData.ts复制）
export interface AvailableVisitStaffMember {
  id: string;
  avatar: string;
  name: string;
  gender: "男" | "女";
  age: number;
  phone: string;
  address: string;
  idCard: string;
  serviceCount: number;
  customerCount: number;
  rating: string;
  joinDate: string;
  availableVisitCount: number;
}

// 可用服务人员API响应
export interface AvailableVisitStaffResponse
  extends ApiResponse<AvailableVisitStaffMember[]> {}
